/**
 * NOTE MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Note non ha enum (a differenza di Task/Call/Meeting): i mapper si
 * limitano a copiare i campi audit e i due campi business (`title`,
 * `description`). Mantenuti come funzioni esplicite per coerenza con il
 * pattern del progetto e per ospitare future espansioni di schema senza
 * dover ricablare l'API.
 */

import { pickAuditFields } from '@crm/shared';
import type { Note, NoteCreatePayload, NoteUpdatePayload } from './noteTypes';

/**
 * Shape del Note come esce dal REST (/o/crm/v1_0/note/{noteId}).
 * Rispecchia lo schema Note nel rest-openapi.yaml.
 */
export interface NoteDto {
  noteId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  title: string;
  description: string;
}

/** DTO -> modello UI. */
export function noteFromDto(dto: NoteDto): Note {
  return {
    noteId: dto.noteId,
    ...pickAuditFields(dto),
    title: dto.title,
    description: dto.description,
  };
}

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare la shape del
 * backend; non invocato dall'app reale.
 */
export function noteToDto(n: Note): NoteDto {
  return {
    noteId: n.noteId,
    ...pickAuditFields(n),
    title: n.title,
    description: n.description,
  };
}

/**
 * Shape del payload accettato dal backend in POST/PATCH. Campi audit e
 * noteId non sono accettati qui (gestiti dal backend).
 */
export interface NoteInput {
  title?: string;
  description?: string;
}

/** Payload create UI -> DTO input. */
export function noteCreateToInput(p: NoteCreatePayload): NoteInput {
  return {
    title: p.title,
    description: p.description,
  };
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function noteUpdateToInput(p: Omit<NoteUpdatePayload, 'noteId'>): NoteInput {
  const out: NoteInput = {};
  if (p.title !== undefined) out.title = p.title;
  if (p.description !== undefined) out.description = p.description;
  return out;
}

/**
 * DTO input (PATCH body) -> Partial<Note> UI. Usato dai mock MSW per
 * applicare il patch al mockNotes storage (shape UI).
 */
export function noteInputFromDto(input: NoteInput): Partial<Note> {
  const out: Partial<Note> = {};
  if (input.title !== undefined) out.title = input.title;
  if (input.description !== undefined) out.description = input.description;
  return out;
}
